import { useState, useMemo } from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
  Alert,
  AlertDescription,
  Button,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { toast } from 'sonner';
import {
  SearchBar,
  PaginationControls,
  FilterTabs,
  TABLE_PAGE_SIZE_OPTIONS,
} from '../components/ListControls';

// ─── Types ─────────────────────────────────────────────────────────────────────

type UpcomingStatus = 'upcoming' | 'overdue' | 'paid';
type HistoryStatus = 'completed' | 'failed';
type PaymentType = 'interest' | 'principal';
type TabValue = 'upcoming' | 'history';
type UpcomingFilterValue = 'all' | UpcomingStatus;
type HistoryFilterValue = 'all' | HistoryStatus;

interface UpcomingPayment {
  id: string;
  date: string;
  amount: number;
  facility: string;
  type: PaymentType;
  status: UpcomingStatus;
}

interface HistoryPayment {
  id: string;
  date: string;
  amount: number;
  facility: string;
  type: PaymentType;
  status: HistoryStatus;
}

// ─── Deterministic Random ─────────────────────────────────────────────────────

function seededRandom(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}

// ─── Mock Data ─────────────────────────────────────────────────────────────────

const FACILITY_NAMES = [
  'USDC Core Facility', 'ABN Yield Facility', 'SNG Stable Facility',
  'EURC Credit Facility', 'GBPT Reserve Facility', 'GYEN Growth Facility',
];

const upcomingPayments: UpcomingPayment[] = (() => {
  const items: UpcomingPayment[] = [];
  const types: PaymentType[] = ['interest', 'principal'];
  const statuses: UpcomingStatus[] = ['upcoming', 'upcoming', 'upcoming', 'overdue', 'paid'];

  for (let i = 0; i < 35; i++) {
    const r = seededRandom(i * 5 + 100);
    const daysOffset = i < 5 ? -Math.round(1 + r * 15) : Math.round(1 + r * 120);
    const baseDate = new Date(2026, 2, 23);
    baseDate.setDate(baseDate.getDate() + daysOffset);
    const dateStr = baseDate.toISOString().split('T')[0];
    const status = daysOffset < 0 ? 'overdue' : statuses[i % statuses.length];
    const amount = parseFloat((200 + seededRandom(i * 5 + 101) * 3500).toFixed(2));

    items.push({
      id: `up-${i + 1}`,
      date: dateStr,
      amount,
      facility: FACILITY_NAMES[i % FACILITY_NAMES.length],
      type: types[i % types.length],
      status,
    });
  }

  return items.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
})();

const historyPayments: HistoryPayment[] = (() => {
  const items: HistoryPayment[] = [];
  const types: PaymentType[] = ['interest', 'principal'];
  const statuses: HistoryStatus[] = ['completed', 'completed', 'completed', 'completed', 'completed', 'failed'];

  for (let i = 0; i < 55; i++) {
    const daysBack = Math.round(1 + seededRandom(i * 5 + 200) * 300);
    const d = new Date(2026, 2, 23);
    d.setDate(d.getDate() - daysBack);
    const dateStr = d.toISOString().split('T')[0];
    const amount = parseFloat((150 + seededRandom(i * 5 + 201) * 4000).toFixed(2));

    items.push({
      id: `hist-${i + 1}`,
      date: dateStr,
      amount,
      facility: FACILITY_NAMES[i % FACILITY_NAMES.length],
      type: types[i % types.length],
      status: statuses[i % statuses.length],
    });
  }

  return items.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
})();

// ─── Helpers ───────────────────────────────────────────────────────────────────

const fmt = (n: number) => n.toLocaleString('en-US', { style: 'currency', currency: 'USD' });

const upcomingStatusConfig: Record<UpcomingStatus, { label: string; className: string }> = {
  upcoming: { label: 'Upcoming', className: 'bg-blue-100 text-blue-700' },
  overdue: { label: 'Overdue', className: 'bg-ascend-error/20 text-ascend-error' },
  paid: { label: 'Paid', className: 'bg-green-100 text-green-700' },
};

const historyStatusConfig: Record<HistoryStatus, { label: string; className: string }> = {
  completed: { label: 'Completed', className: 'bg-green-100 text-green-700' },
  failed: { label: 'Failed', className: 'bg-ascend-error/20 text-ascend-error' },
};

const typeConfig: Record<PaymentType, { label: string; className: string }> = {
  interest: { label: 'Interest', className: 'bg-purple-100 text-purple-700' },
  principal: { label: 'Principal', className: 'bg-indigo-100 text-indigo-700' },
};

const UPCOMING_FILTERS: { value: UpcomingFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'upcoming', label: 'Upcoming' },
  { value: 'overdue', label: 'Overdue' },
  { value: 'paid', label: 'Paid' },
];

const HISTORY_FILTERS: { value: HistoryFilterValue; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'completed', label: 'Completed' },
  { value: 'failed', label: 'Failed' },
];

// ─── Page ──────────────────────────────────────────────────────────────────────

export function PaymentsPage() {
  const [tab, setTab] = useState<TabValue>('upcoming');
  const [autoPay, setAutoPay] = useState(true);

  // Upcoming state
  const [upcomingSearch, setUpcomingSearch] = useState('');
  const [upcomingFilter, setUpcomingFilter] = useState<UpcomingFilterValue>('all');
  const [upcomingPage, setUpcomingPage] = useState(1);
  const [upcomingPageSize, setUpcomingPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // History state
  const [historySearch, setHistorySearch] = useState('');
  const [historyFilter, setHistoryFilter] = useState<HistoryFilterValue>('all');
  const [historyPage, setHistoryPage] = useState(1);
  const [historyPageSize, setHistoryPageSize] = useState<number>(TABLE_PAGE_SIZE_OPTIONS[0]);

  // ── Upcoming derived data ──

  const upcomingCounts = useMemo<Record<UpcomingFilterValue, number>>(() => ({
    all: upcomingPayments.length,
    upcoming: upcomingPayments.filter((p) => p.status === 'upcoming').length,
    overdue: upcomingPayments.filter((p) => p.status === 'overdue').length,
    paid: upcomingPayments.filter((p) => p.status === 'paid').length,
  }), []);

  const filteredUpcoming = useMemo(() => {
    let items = upcomingPayments;
    if (upcomingFilter !== 'all') {
      items = items.filter((p) => p.status === upcomingFilter);
    }
    if (upcomingSearch.trim()) {
      const q = upcomingSearch.toLowerCase();
      items = items.filter((p) => p.facility.toLowerCase().includes(q));
    }
    return items;
  }, [upcomingSearch, upcomingFilter]);

  const paginatedUpcoming = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredUpcoming.length / upcomingPageSize));
    const safe = Math.min(upcomingPage, totalPages);
    return filteredUpcoming.slice((safe - 1) * upcomingPageSize, safe * upcomingPageSize);
  }, [filteredUpcoming, upcomingPage, upcomingPageSize]);

  // ── History derived data ──

  const historyCounts = useMemo<Record<HistoryFilterValue, number>>(() => ({
    all: historyPayments.length,
    completed: historyPayments.filter((p) => p.status === 'completed').length,
    failed: historyPayments.filter((p) => p.status === 'failed').length,
  }), []);

  const filteredHistory = useMemo(() => {
    let items = historyPayments;
    if (historyFilter !== 'all') {
      items = items.filter((p) => p.status === historyFilter);
    }
    if (historySearch.trim()) {
      const q = historySearch.toLowerCase();
      items = items.filter((p) => p.facility.toLowerCase().includes(q));
    }
    return items;
  }, [historySearch, historyFilter]);

  const paginatedHistory = useMemo(() => {
    const totalPages = Math.max(1, Math.ceil(filteredHistory.length / historyPageSize));
    const safe = Math.min(historyPage, totalPages);
    return filteredHistory.slice((safe - 1) * historyPageSize, safe * historyPageSize);
  }, [filteredHistory, historyPage, historyPageSize]);

  // ── Stat data ──

  const overduePayments = useMemo(
    () => upcomingPayments.filter((p) => p.status === 'overdue'),
    [],
  );

  const nextPayment = useMemo(() => {
    const upcoming = upcomingPayments.find((p) => p.status === 'upcoming');
    return upcoming ?? null;
  }, []);

  const totalDueThisMonth = useMemo(() => {
    const now = new Date();
    const monthStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    return upcomingPayments
      .filter((p) => p.date.startsWith(monthStr) && p.status !== 'paid')
      .reduce((sum, p) => sum + p.amount, 0);
  }, []);

  const paymentsMade = useMemo(
    () => historyPayments.filter((p) => p.status === 'completed').length,
    [],
  );

  const TABS: { value: TabValue; label: string }[] = [
    { value: 'upcoming', label: 'Upcoming' },
    { value: 'history', label: 'History' },
  ];

  return (
    <div className="space-y-6 sm:space-y-8">
      {/* Page header */}
      <div className="flex items-start gap-3">
        <Icon name="streamline:receipt" className="mt-1 h-6 w-6 sm:h-7 sm:w-7 text-foreground flex-shrink-0" />
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Payments</h1>
          <p className="text-sm text-muted-foreground">View upcoming payments and payment history.</p>
        </div>
      </div>

      {/* Stat cards */}
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 lg:gap-4">
        <StatCard
          title="Next Payment"
          value={nextPayment ? fmt(nextPayment.amount) : '-'}
          subtitle={nextPayment ? nextPayment.date : 'No upcoming'}
          icon={<Icon name="streamline:blank-calendar" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Due This Month"
          value={fmt(totalDueThisMonth)}
          subtitle="Outstanding balance"
          icon={<Icon name="streamline:money-cash-dollar-coin-accounting-billing-payment-cash-coin-currency-money-finance" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Payments Made"
          value={paymentsMade.toString()}
          subtitle="All time completed"
          icon={<Icon name="streamline:interface-validation-check-circle-checkmark-addition-circle-success-check-validation-add-form" className="h-5 w-5 text-muted-foreground" />}
        />
        <StatCard
          title="Auto-Pay"
          value={autoPay ? 'Enabled' : 'Disabled'}
          subtitle={autoPay ? 'Payments auto-processed' : 'Manual payments only'}
          icon={<Icon name="streamline:flash-1" className="h-5 w-5 text-muted-foreground" />}
        />
      </div>

      {/* Overdue alert */}
      {overduePayments.length > 0 && tab === 'upcoming' && (
        <Alert className="border-ascend-error/30 bg-ascend-error/10">
          <Icon name="streamline:warning-triangle" className="h-4 w-4 text-ascend-error" />
          <AlertDescription className="text-ascend-error">
            You have {overduePayments.length} overdue payment{overduePayments.length > 1 ? 's' : ''} totalling {fmt(overduePayments.reduce((s, p) => s + p.amount, 0))}. Please pay immediately to avoid penalties.
          </AlertDescription>
        </Alert>
      )}

      {/* Tab toggle */}
      <div className="inline-flex rounded-lg bg-muted p-1">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => { setTab(t.value); }}
            className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
              tab === t.value
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Upcoming Tab ─────────────────────────────────────────────────────── */}
      {tab === 'upcoming' && (
        <div className="space-y-4">
          {/* Auto-pay toggle */}
          <div className="flex items-center justify-between rounded-xl border border-border p-4 sm:p-5">
            <div>
              <p className="text-sm font-medium">Auto-Pay</p>
              <p className="text-xs text-muted-foreground">Automatically process payments on their due date.</p>
            </div>
            <button
              onClick={() => { setAutoPay((v) => !v); toast.success(autoPay ? 'Auto-pay disabled' : 'Auto-pay enabled'); }}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors ${autoPay ? 'bg-primary' : 'bg-muted-foreground/30'}`}
            >
              <span className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow-sm transition-transform ${autoPay ? 'translate-x-5' : 'translate-x-0'}`} />
            </button>
          </div>

          {/* Search + Filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <SearchBar
              value={upcomingSearch}
              onChange={(v) => { setUpcomingSearch(v); setUpcomingPage(1); }}
              placeholder="Search by facility..."
              className="sm:max-w-sm"
            />
            <FilterTabs
              filters={UPCOMING_FILTERS}
              active={upcomingFilter}
              onChange={(v) => { setUpcomingFilter(v); setUpcomingPage(1); }}
              counts={upcomingCounts}
            />
          </div>

          <Card>
            <CardHeader className="p-6">
              <CardTitle>Upcoming Payments</CardTitle>
              <CardDescription>Interest and principal payments due on your positions.</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Amount</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Facility</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Type</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Status</th>
                      <th className="pb-3 text-right font-medium text-muted-foreground">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedUpcoming.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="py-12 text-center text-sm text-muted-foreground">
                          {upcomingSearch.trim() || upcomingFilter !== 'all'
                            ? 'No payments matching your filters.'
                            : 'No upcoming payments.'}
                        </td>
                      </tr>
                    ) : (
                      paginatedUpcoming.map((p) => {
                        const statusCfg = upcomingStatusConfig[p.status];
                        const typeCfg = typeConfig[p.type];
                        return (
                          <tr key={p.id} className={`border-b border-border last:border-0 ${p.status === 'overdue' ? 'bg-ascend-error/5' : ''}`}>
                            <td className="py-3 whitespace-nowrap">{p.date}</td>
                            <td className="py-3 font-semibold whitespace-nowrap">{fmt(p.amount)}</td>
                            <td className="py-3 whitespace-nowrap">{p.facility}</td>
                            <td className="py-3">
                              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${typeCfg.className}`}>{typeCfg.label}</span>
                            </td>
                            <td className="py-3">
                              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusCfg.className}`}>{statusCfg.label}</span>
                            </td>
                            <td className="py-3 text-right">
                              {p.status !== 'paid' && (
                                <Button size="sm" onClick={() => toast.success('Payment initiated')}>Pay Now</Button>
                              )}
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {paginatedUpcoming.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-12 text-center">
                    <p className="text-sm text-muted-foreground">
                      {upcomingSearch.trim() || upcomingFilter !== 'all'
                        ? 'No payments matching your filters.'
                        : 'No upcoming payments.'}
                    </p>
                  </div>
                ) : (
                  paginatedUpcoming.map((p) => {
                    const statusCfg = upcomingStatusConfig[p.status];
                    const typeCfg = typeConfig[p.type];
                    return (
                      <div key={p.id} className={`rounded-xl border p-4 space-y-3 ${p.status === 'overdue' ? 'border-ascend-error/40 bg-ascend-error/5' : 'border-border'}`}>
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold">{fmt(p.amount)}</p>
                            <p className="text-xs text-muted-foreground">{p.date}</p>
                          </div>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusCfg.className}`}>{statusCfg.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{p.facility}</span>
                          <span>·</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${typeCfg.className}`}>{typeCfg.label}</span>
                        </div>
                        {p.status !== 'paid' && (
                          <Button size="sm" className="w-full" onClick={() => toast.success('Payment initiated')}>Pay Now</Button>
                        )}
                      </div>
                    );
                  })
                )}
              </div>

              <PaginationControls
                page={upcomingPage}
                setPage={setUpcomingPage}
                pageSize={upcomingPageSize}
                setPageSize={setUpcomingPageSize}
                totalItems={filteredUpcoming.length}
                label="payments"
              />
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── History Tab ──────────────────────────────────────────────────────── */}
      {tab === 'history' && (
        <div className="space-y-4">
          {/* Search + Filters */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <SearchBar
              value={historySearch}
              onChange={(v) => { setHistorySearch(v); setHistoryPage(1); }}
              placeholder="Search by facility name..."
              className="sm:max-w-sm"
            />
            <FilterTabs
              filters={HISTORY_FILTERS}
              active={historyFilter}
              onChange={(v) => { setHistoryFilter(v); setHistoryPage(1); }}
              counts={historyCounts}
            />
          </div>

          <Card>
            <CardHeader className="p-6">
              <CardTitle>Payment History</CardTitle>
              <CardDescription>Past payments with date, amount, facility, and status.</CardDescription>
            </CardHeader>
            <CardContent className="p-6">
              {/* Desktop */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="pb-3 text-left font-medium text-muted-foreground">Date</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Amount</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Facility</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Type</th>
                      <th className="pb-3 text-left font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedHistory.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="py-12 text-center text-sm text-muted-foreground">
                          {historySearch.trim() || historyFilter !== 'all'
                            ? 'No payments matching your filters.'
                            : 'No payment history yet.'}
                        </td>
                      </tr>
                    ) : (
                      paginatedHistory.map((p) => {
                        const statusCfg = historyStatusConfig[p.status];
                        const typeCfg = typeConfig[p.type];
                        return (
                          <tr key={p.id} className="border-b border-border last:border-0">
                            <td className="py-3 whitespace-nowrap">{p.date}</td>
                            <td className="py-3 font-semibold whitespace-nowrap">{fmt(p.amount)}</td>
                            <td className="py-3 whitespace-nowrap">{p.facility}</td>
                            <td className="py-3">
                              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${typeCfg.className}`}>{typeCfg.label}</span>
                            </td>
                            <td className="py-3">
                              <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusCfg.className}`}>{statusCfg.label}</span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {paginatedHistory.length === 0 ? (
                  <div className="rounded-xl border border-dashed border-border p-12 text-center">
                    <p className="text-sm text-muted-foreground">
                      {historySearch.trim() || historyFilter !== 'all'
                        ? 'No payments matching your filters.'
                        : 'No payment history yet.'}
                    </p>
                  </div>
                ) : (
                  paginatedHistory.map((p) => {
                    const statusCfg = historyStatusConfig[p.status];
                    const typeCfg = typeConfig[p.type];
                    return (
                      <div key={p.id} className="rounded-xl border border-border p-4 space-y-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <p className="font-semibold">{fmt(p.amount)}</p>
                            <p className="text-xs text-muted-foreground">{p.date}</p>
                          </div>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${statusCfg.className}`}>{statusCfg.label}</span>
                        </div>
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <span>{p.facility}</span>
                          <span>·</span>
                          <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${typeCfg.className}`}>{typeCfg.label}</span>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <PaginationControls
                page={historyPage}
                setPage={setHistoryPage}
                pageSize={historyPageSize}
                setPageSize={setHistoryPageSize}
                totalItems={filteredHistory.length}
                label="payments"
              />
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function StatCard({ title, value, subtitle, icon }: { title: string; value: string; subtitle?: string; icon: React.ReactNode }) {
  return (
    <div className="rounded-xl border border-border p-5 sm:p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs sm:text-sm text-muted-foreground">{title}</span>
        {icon}
      </div>
      <div className="mt-2 text-2xl sm:text-3xl font-bold">{value}</div>
      {subtitle && <div className="mt-1 text-xs sm:text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}
